import type { VideoEditDraft } from '../types/videoEditDraft';

export const AIRECORDING_DRAFTS_REFRESH_EVENT = 'airecording-drafts-refresh';

const MAX_DRAFTS = 100;

export function broadcastDraftsRefresh(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AIRECORDING_DRAFTS_REFRESH_EVENT));
}

export function subscribeDraftsRefresh(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(AIRECORDING_DRAFTS_REFRESH_EVENT, callback);
  return () => window.removeEventListener(AIRECORDING_DRAFTS_REFRESH_EVENT, callback);
}

function storageKey(userEmail: string, slug: string): string {
  return `airecording:videoDrafts:${slug}:${userEmail}`;
}

export function loadDrafts(
  userEmail: string | null | undefined,
  slug: string | null | undefined,
): VideoEditDraft[] {
  if (!userEmail) return [];
  const s = slug || 'default';
  try {
    const raw = localStorage.getItem(storageKey(userEmail, s));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as VideoEditDraft[]) : [];
  } catch {
    return [];
  }
}

function persistList(userEmail: string, slug: string, drafts: VideoEditDraft[]): void {
  localStorage.setItem(storageKey(userEmail, slug), JSON.stringify(drafts.slice(0, MAX_DRAFTS)));
  broadcastDraftsRefresh();
}

export function getVideoEditDraft(
  userEmail: string | null | undefined,
  slug: string | null | undefined,
  id: string,
): VideoEditDraft | null {
  return loadDrafts(userEmail, slug).find(d => d.id === id) ?? null;
}

export function upsertVideoEditDraft(
  userEmail: string,
  slug: string | null | undefined,
  draft: VideoEditDraft,
): void {
  const s = slug || 'default';
  const list = loadDrafts(userEmail, slug);
  const idx = list.findIndex(d => d.id === draft.id);
  if (idx >= 0) list[idx] = draft;
  else list.unshift(draft);
  persistList(userEmail, s, list);
}

export function deleteVideoEditDraft(
  userEmail: string,
  slug: string | null | undefined,
  id: string,
): void {
  const s = slug || 'default';
  const list = loadDrafts(userEmail, slug).filter(d => d.id !== id);
  persistList(userEmail, s, list);
}
